import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { superAdminService } from '@/services/storageService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import VerticalAppLayout from '@/components/layout/VerticalAppLayout';
import aivLogo from '@/assets/aiv-logo-trans.png';

type PolicyType = 'privacy_policy' | 'terms_and_conditions';

type DashboardData = Awaited<ReturnType<typeof superAdminService.getDashboard>>;
type PolicyData = Awaited<ReturnType<typeof superAdminService.getPolicies>>;
type AuditData = Awaited<ReturnType<typeof superAdminService.getAuditLogs>>;

const policyLabel = (value: PolicyType) =>
  value === 'privacy_policy' ? 'Privacy Policy' : 'Terms and Conditions';

const parsePaymentDetails = (details?: string | null) => {
  const out: Record<string, string> = {};
  if (!details) return out;
  for (const part of details.split(',')) {
    const [rawKey, ...rawVal] = part.split('=');
    const key = (rawKey || '').trim();
    const val = rawVal.join('=').trim();
    if (!key) continue;
    out[key] = val;
  }
  return out;
};

export default function SuperAdmin() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [policies, setPolicies] = useState<PolicyData | null>(null);
  const [audit, setAudit] = useState<AuditData | null>(null);
  const [packageFilter, setPackageFilter] = useState<'all' | 'PACKAGE1' | 'PACKAGE2' | 'PACKAGE3'>('all');
  const [clubsPage, setClubsPage] = useState(1);
  const [clubsPageSize, setClubsPageSize] = useState(20);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(20);
  const [activeSection, setActiveSection] = useState<'home' | 'policies'>('home');

  const [activePolicy, setActivePolicy] = useState<PolicyType>('privacy_policy');
  const [title, setTitle] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [version, setVersion] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const currentPolicy = useMemo(() => {
    return policies?.policies.find((p) => p.policy_type === activePolicy) ?? null;
  }, [policies, activePolicy]);

  const loadAll = async () => {
    setIsLoading(true);
    const [dashRes, polRes, auditRes] = await Promise.allSettled([
      superAdminService.getDashboard({
        package: packageFilter === 'all' ? undefined : packageFilter,
        page: clubsPage,
        page_size: clubsPageSize,
      }),
      superAdminService.getPolicies(),
      superAdminService.getAuditLogs({ page: auditPage, page_size: auditPageSize }),
    ]);

    if (dashRes.status === 'fulfilled') {
      setDashboard(dashRes.value);
    } else {
      toast.error(dashRes.reason instanceof Error ? dashRes.reason.message : t('superadmin_failed_load_billing_snapshot', 'Failed to load billing snapshot'));
    }

    if (polRes.status === 'fulfilled') {
      setPolicies(polRes.value);
    } else {
      toast.error(polRes.reason instanceof Error ? polRes.reason.message : t('superadmin_failed_load_policies', 'Failed to load policies'));
    }

    if (auditRes.status === 'fulfilled') {
      setAudit(auditRes.value);
    } else {
      toast.error(auditRes.reason instanceof Error ? auditRes.reason.message : t('superadmin_failed_load_failed_payments', 'Failed to load failed payments'));
    }

    setIsLoading(false);
  };

  useEffect(() => {
    void loadAll();
  }, [packageFilter, clubsPage, clubsPageSize, auditPage, auditPageSize]);

  useEffect(() => {
    if (!currentPolicy) return;
    setTitle(currentPolicy.draft_title || currentPolicy.active_title || '');
    const nextHtml = currentPolicy.draft_content_html || currentPolicy.active_content_html || '';
    setEditorHtml(nextHtml);
    setVersion('');
    setEffectiveAt('');
  }, [currentPolicy?.policy_type, currentPolicy?.draft_updated_at, currentPolicy?.active_version]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const saveDraft = async () => {
    if (!title.trim()) {
      toast.error(t('superadmin_policy_title_required', 'Policy title is required'));
      return;
    }
    setIsSaving(true);
    try {
      await superAdminService.saveDraft(activePolicy, {
        title: title.trim(),
        content_html: editorHtml,
      });
      toast.success(t('superadmin_draft_saved', 'Draft saved'));
      await loadAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('superadmin_could_not_save_draft', 'Could not save draft'));
    } finally {
      setIsSaving(false);
    }
  };

  const publishPolicy = async () => {
    if (!title.trim()) {
      toast.error(t('superadmin_policy_title_required', 'Policy title is required'));
      return;
    }
    if (!version.trim()) {
      toast.error(t('superadmin_version_required', 'Version is required'));
      return;
    }
    setIsPublishing(true);
    try {
      await superAdminService.publishPolicy(activePolicy, {
        version: version.trim(),
        title: title.trim(),
        content_html: editorHtml,
        effective_at: effectiveAt ? new Date(effectiveAt).toISOString() : undefined,
      });
      toast.success(t('superadmin_policy_published', 'Policy published'));
      await loadAll();
      setVersion('');
      setEffectiveAt('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('superadmin_could_not_publish_policy', 'Could not publish policy'));
    } finally {
      setIsPublishing(false);
    }
  };

  if (!user?.isSuperAdmin) {
    return null;
  }

  return (
    <VerticalAppLayout
      mobileTitle="Super Admin"
      sidebarHeader={(
        <div className="p-5">
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <img
              src={aivLogo}
              alt="SmashLytics Logo"
              className="h-36 w-36 object-contain"
            />
            <h1 className="text-2xl font-bold leading-tight">SmashLytics</h1>
          </div>
        </div>
      )}
      sidebarNav={(
        <>
          <button
            type="button"
            onClick={() => setActiveSection('home')}
            className={`block w-full rounded-md px-3 py-2 text-left text-xl font-semibold transition ${
              activeSection === 'home'
                ? 'bg-sidebar-accent text-sidebar-foreground'
                : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-foreground'
            }`}
          >
            Home
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('policies')}
            className={`block w-full rounded-md px-3 py-2 text-left text-xl font-semibold transition ${
              activeSection === 'policies'
                ? 'bg-sidebar-accent text-sidebar-foreground'
                : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-foreground'
            }`}
          >
            Policies
          </button>
        </>
      )}
      sidebarFooter={(
        <div className="space-y-3">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-xl font-semibold text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <LogOut size={28} className="mr-3" />
            Logout
          </Button>
          <div className="text-base text-sidebar-foreground/70 text-center">
            Powered by{' '}
            <a
              href="https://aivstudios.com/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Aiv Studios
            </a>
            .
            <br />
            All rights reserved © 2026.
          </div>
        </div>
      )}
    >
      <div className="w-full px-4 py-6 text-base md:px-8 md:text-lg">
        <div className="flex w-full flex-col gap-6">
          {activeSection === 'home' && (
          <>
          <section id="home-section" className="scroll-mt-6">
            <div>
              <h2 className="text-4xl font-bold">Home</h2>
              <p className="text-lg text-muted-foreground">Platform subscriptions, policy publishing, and audit trail</p>
            </div>
          </section>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl">Total Clubs</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-bold">{dashboard?.total_clubs ?? 0}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl">Total Members</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-bold">{dashboard?.total_members ?? 0}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl">Active Subscriptions</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-bold">{dashboard?.active_subscriptions ?? 0}</CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Club Billing Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="package-filter">Package</Label>
                <select
                  id="package-filter"
                  value={packageFilter}
                  onChange={(e) => {
                    setPackageFilter(e.target.value as 'all' | 'PACKAGE1' | 'PACKAGE2' | 'PACKAGE3');
                    setClubsPage(1);
                  }}
                  className="h-11 rounded-md border bg-background px-3 text-base"
                >
                  <option value="all">All packages</option>
                  <option value="PACKAGE1">Starter (PACKAGE1)</option>
                  <option value="PACKAGE2">Pro (PACKAGE2)</option>
                  <option value="PACKAGE3">Elite (PACKAGE3)</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="club-page-size">Rows per page</Label>
                <select
                  id="club-page-size"
                  value={String(clubsPageSize)}
                  onChange={(e) => {
                    setClubsPageSize(Number(e.target.value));
                    setClubsPage(1);
                  }}
                  className="h-11 rounded-md border bg-background px-3 text-base"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full min-w-[1360px] text-left text-base">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 pr-3">Club</th>
                    <th className="py-2 pr-3">Subscriber First Name</th>
                    <th className="py-2 pr-3">Subscriber Last Name</th>
                    <th className="py-2 pr-3">Subscriber Email</th>
                    <th className="py-2 pr-3">Sport</th>
                    <th className="py-2 pr-3">Members</th>
                    <th className="py-2 pr-3">Active Players</th>
                    <th className="py-2 pr-3">Package</th>
                    <th className="py-2 pr-3">Billing</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Trial Ends</th>
                    <th className="py-2 pr-3">Last Payment</th>
                    <th className="py-2 pr-3">Next Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.clubs.map((club) => (
                    <tr key={club.club_id} className="border-b align-top">
                      <td className="py-2 pr-3">{club.club_name}</td>
                      <td className="py-2 pr-3">{club.payer_first_name || '-'}</td>
                      <td className="py-2 pr-3">{club.payer_last_name || '-'}</td>
                      <td className="py-2 pr-3">{club.payer_email || '-'}</td>
                      <td className="py-2 pr-3">{club.sport_type || '-'}</td>
                      <td className="py-2 pr-3">{club.total_members}</td>
                      <td className="py-2 pr-3">{club.active_players}</td>
                      <td className="py-2 pr-3">{club.package_code || '-'}</td>
                      <td className="py-2 pr-3">{club.billing_cycle || '-'}</td>
                      <td className="py-2 pr-3">{club.subscription_status || '-'}</td>
                      <td className="py-2 pr-3">
                        {club.trial_end ? new Date(club.trial_end).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2 pr-3">
                        {club.last_payment_amount != null
                          ? `${club.last_payment_currency || 'cad'} ${Number(club.last_payment_amount / 100).toFixed(2)}`
                          : '-'}
                      </td>
                      <td className="py-2 pr-3">
                        {club.next_payment_at ? new Date(club.next_payment_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                  {!dashboard?.clubs.length && !isLoading && (
                    <tr>
                      <td className="py-4 text-muted-foreground" colSpan={13}>No clubs found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-base text-muted-foreground">
                Page {dashboard?.page || 1} of {dashboard?.total_pages || 1} | Total {dashboard?.total_items || 0} clubs
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setClubsPage((p) => Math.max(1, p - 1))}
                  disabled={(dashboard?.page || 1) <= 1}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setClubsPage((p) => p + 1)}
                  disabled={(dashboard?.page || 1) >= (dashboard?.total_pages || 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Failed Payments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="audit-page-size">Rows per page</Label>
                <select
                  id="audit-page-size"
                  value={String(auditPageSize)}
                  onChange={(e) => {
                    setAuditPageSize(Number(e.target.value));
                    setAuditPage(1);
                  }}
                  className="h-11 rounded-md border bg-background px-3 text-base"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full min-w-[760px] text-left text-base">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 pr-3">Failure Time</th>
                    <th className="py-2 pr-3">Payer Email</th>
                    <th className="py-2 pr-3">Invoice ID</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Amount Due</th>
                    <th className="py-2 pr-3">Amount Paid</th>
                    <th className="py-2 pr-3">Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit?.items || []).map((item) => {
                    const parsed = parsePaymentDetails(item.details);
                    const amountDueRaw = parsed.amount_due ?? '';
                    const amountPaidRaw = parsed.amount_paid ?? '';
                    const amountDue = amountDueRaw !== '' ? (Number(amountDueRaw) / 100).toFixed(2) : '-';
                    const amountPaid = amountPaidRaw !== '' ? (Number(amountPaidRaw) / 100).toFixed(2) : '-';
                    return (
                      <tr key={item.id} className="border-b align-top">
                        <td className="py-2 pr-3">{new Date(item.created_at).toLocaleString()}</td>
                        <td className="py-2 pr-3">{item.actor_email || '-'}</td>
                        <td className="py-2 pr-3">{item.target_id || parsed.invoice || '-'}</td>
                        <td className="py-2 pr-3">{parsed.status || '-'}</td>
                        <td className="py-2 pr-3">{amountDue}</td>
                        <td className="py-2 pr-3">{amountPaid}</td>
                        <td className="py-2 pr-3">{(parsed.currency || '').toUpperCase() || '-'}</td>
                      </tr>
                    );
                  })}
                  {!audit?.items.length && !isLoading && (
                    <tr>
                      <td className="py-4 text-muted-foreground" colSpan={7}>No failed payments yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-base text-muted-foreground">
                Page {audit?.page || 1} of {audit?.total_pages || 1} | Total {audit?.total_items || 0} failed payments
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                  disabled={(audit?.page || 1) <= 1}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAuditPage((p) => p + 1)}
                  disabled={(audit?.page || 1) >= (audit?.total_pages || 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
        )}

        {activeSection === 'policies' && (
        <section id="policies-section" className="scroll-mt-6">
          <div className="mb-3">
            <h3 className="text-3xl font-bold">Policies</h3>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Policy Editor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={activePolicy === 'privacy_policy' ? 'default' : 'outline'}
                  onClick={() => setActivePolicy('privacy_policy')}
                >
                  Privacy Policy
                </Button>
                <Button
                  type="button"
                  variant={activePolicy === 'terms_and_conditions' ? 'default' : 'outline'}
                  onClick={() => setActivePolicy('terms_and_conditions')}
                >
                  Terms and Conditions
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="policy-title">Title</Label>
                  <Input
                    id="policy-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={`Enter ${policyLabel(activePolicy)} title`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Published Version</Label>
                  <div className="rounded-md border px-3 py-2 text-base">
                    {currentPolicy?.active_version || 'Not published yet'}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Policy Content (HTML editor)</Label>
                <Textarea
                  value={editorHtml}
                  onChange={(e) => setEditorHtml(e.target.value)}
                  className="min-h-[320px] font-mono text-sm"
                  placeholder="<h1>Heading</h1><p>Paragraph with <i>italic</i> and <strong>bold</strong>.</p>"
                />
                <p className="text-sm text-muted-foreground">
                  Write valid HTML directly. It will be sanitized for safety and rendered on the public policy pages.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="policy-version">Publish Version (example: 2026-03-03)</Label>
                  <Input
                    id="policy-version"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="Enter new policy version"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="policy-effective-at">Effective At (optional)</Label>
                  <Input
                    id="policy-effective-at"
                    type="datetime-local"
                    value={effectiveAt}
                    onChange={(e) => setEffectiveAt(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="outline" onClick={saveDraft} disabled={isSaving || isPublishing}>
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button type="button" onClick={publishPolicy} disabled={isPublishing || isSaving}>
                  {isPublishing ? 'Publishing...' : 'Publish Policy'}
                </Button>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-muted-foreground">
                <div>Draft updated at: {currentPolicy?.draft_updated_at ? new Date(currentPolicy.draft_updated_at).toLocaleString() : '-'}</div>
                <div>Draft updated by: {currentPolicy?.draft_updated_by_email || '-'}</div>
                <div>
                  Active effective at: {currentPolicy?.active_effective_at ? new Date(currentPolicy.active_effective_at).toLocaleString() : '-'}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
        )}
        </div>
      </div>
    </VerticalAppLayout>
  );
}
